import axios from "axios";
import { ChartData, Chart as ChartJS, ChartOptions } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useMemo, useState, useRef } from "react";
import { Chart } from 'react-chartjs-2';
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { APP_BASE_URL, GET_ASSET_HEALTH, GET_DEVICE_TYPE_ASSET } from "../../Common/EndPoints";
import { selectedCustomersSelector } from "../../features/authentication/auth-slice";
import "./asset-health.scss";
import { useNavigate } from "react-router-dom";
ChartJS.register(ChartDataLabels);

enum AssetHealthEnum {
    "Expected Range" = "EXPECTED_RANGE",
    "Permissible Range" = "PERMISSIBLE_RANGE",
    "Outside Permissible Range" = "OUTSIDE_PERMISSIBLE_RANGE",
}

// {
//     customerName: "DER",
//     healthyCount: 181,
//     warningCount: 793,
//     criticalCount: 19,
// }

const colors = {
    [AssetHealthEnum["Expected Range"]]: 'rgba(72, 235, 54, 0.8)',
    [AssetHealthEnum["Permissible Range"]]: 'rgba(192, 188, 75, 0.8)',
    [AssetHealthEnum["Outside Permissible Range"]]: 'rgba(255, 93, 64, 0.8)',
}

const getMainChartData = (assetData: any) => {
    const data = assetData || [];
    const chartData: ChartData<"bar"> = {
        labels: data.map((item: any) => item.customerName),
        datasets: [
            {
                label: 'Expected Range',
                data: data.map((item: any) => item.healthyCount),
                backgroundColor: colors[AssetHealthEnum["Expected Range"]],
                barThickness: "flex",
            },
            {
                label: 'Permissible Range',
                data: data.map((item: any) => item.warningCount),
                backgroundColor: colors[AssetHealthEnum["Permissible Range"]],
                barThickness: "flex",
            },
            {
                label: 'Outside Permissible Range',
                data: data.map((item: any) => item.criticalCount),
                backgroundColor: colors[AssetHealthEnum["Outside Permissible Range"]],
                barThickness: "flex",
            }
        ]
    }
    return chartData;
}

const getAssetHealthTypeLabel = (assetType: string) => {
    switch (assetType) {
        case AssetHealthEnum["Expected Range"]:
            return "Expected Range";
        case AssetHealthEnum["Permissible Range"]:
            return "Permissible Range";
        case AssetHealthEnum["Outside Permissible Range"]:
            return "Outside Permissible Range";
        default:
            return "";
    }
}

const getChildChartData = (assetHealthData: any, assetHealthType: string) => {
    let data = [];
    switch (assetHealthType) {
        case AssetHealthEnum["Expected Range"]:
            data = assetHealthData.map((item: any) => item.healthyCount);
            break;
        case AssetHealthEnum["Permissible Range"]:
            data = assetHealthData.map((item: any) => item.warningCount);
            break;
        case AssetHealthEnum["Outside Permissible Range"]:
            data = assetHealthData.map((item: any) => item.criticalCount);
            break;
        default:
            data = [];
    }
    const chartData: ChartData<"bar"> = {
        labels: assetHealthData.map((item: any) => item.deviceType),
        datasets: [
            {
                label: getAssetHealthTypeLabel(assetHealthType),
                data: data,
                backgroundColor: colors[assetHealthType as keyof typeof colors],
                barThickness: "flex",
            },

        ]
    }
    return chartData;
}

type AssetHealthChartAttribute = {
    assetHealthType: "EXPECTED_RANGE" | "PERMISSIBLE_RANGE" | "OUTSIDE_PERMISSIBLE_RANGE" | null;
    station: string | null;
}

const initialAssetHealthChartAttribute: AssetHealthChartAttribute = {
    assetHealthType: null,
    station: null
}

const AssetHealthWidget = ({ selectedStations }: { selectedStations: string }) => {
    const navigate = useNavigate();
    const selectedCustomersList = useSelector(selectedCustomersSelector);
    const selectedCustomersListByProp = useMemo(() => {
        return selectedStations.split(",")
    }, [selectedStations]);

    const singleCustomer = selectedCustomersListByProp.length === 1 ? selectedCustomersList?.find((customer) => customer.customerId === selectedCustomersListByProp[0]) : null;

    const [assetHealthData, setAssetHealthData] = useState<any[]>([]);
    const mainChartData = useMemo(() => {
        return getMainChartData(assetHealthData);
    }, [assetHealthData])

    const [assetHealthStats, setAssetHealthStats] = useState({
        healthyCount: 0,
        warningCount: 0,
        criticalCount: 0,
    })

    const [assetHealthForStation, setAssetHealthForStation] = useState<any>();
    const [assetHealthChartAttribute, setAssetHealthChartAttribute] = useState<AssetHealthChartAttribute>(initialAssetHealthChartAttribute);
    const [deviceTypeList, setDeviceTypeList] = useState<any[]>([]);
    const childChartRef = useRef<any>(null);

    const getDeviceTypes = () => {
        let url = `${APP_BASE_URL}${GET_DEVICE_TYPE_ASSET}`;
        axios.get(url).then((res) => {
            let deviceTypes = res.data.data.map((type: any) => ({
                value: type.typeid,
                label: type.type,
                alias: type.label,
            }))
            setDeviceTypeList(deviceTypes)
        });
    };

    useEffect(() => {
        getDeviceTypes();
    }, []);

    const mainChartOptions = useMemo(() => {
        const getMaxValue = () => {
            let y_max = 0;
            const values = (assetHealthData || []).map((item: any) => (
                [item.healthyCount, item.warningCount, item.criticalCount]
            ));

            const maxValue = Math.max(...values.flat(), 0);
            y_max = maxValue * 1.2;
            return y_max;
        }

        const y_max = getMaxValue();

        const options: ChartOptions<"bar"> = {
            responsive: true,
            maintainAspectRatio: false,
            onClick: (event, elements, chart) => {
                handleChartClick(event, elements, chart)
            },
            datasets: {
                bar: {
                    barPercentage: 0.5,
                    borderWidth: 2,
                    borderColor: 'rgba(255, 255, 255)',
                }
            },
            plugins: {
                legend: { position: 'top' },
                datalabels: {
                    anchor: 'end',
                    align: 'top',
                    font: { weight: 'bold' },
                    formatter: v => v?.toLocaleString?.()
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: { callback: v => v.toLocaleString() },
                    suggestedMax: y_max,
                },
            }
        };

        return options;
    }, [assetHealthData])

    useEffect(() => {
        if (selectedCustomersListByProp.length > 0) {
            getAssetsHealthData(selectedCustomersListByProp, "", "");
            setAssetHealthForStation(null);
            setAssetHealthChartAttribute(initialAssetHealthChartAttribute);
        }
    }, [selectedCustomersListByProp]);

    const handleChartClick = async (_event: any, elements: any, chart: any) => {
        if (elements.length > 0) {
            const stationLabel = chart.tooltip?.dataPoints[0]?.label;
            const dataPointLabel = chart.tooltip?.dataPoints[0]?.dataset?.label;

            const stationCustomerId = selectedCustomersList.find((customer) =>
                customer.customerName === stationLabel
            )?.customerId;


            if (stationCustomerId) {
                await getAssetsHealthData([stationCustomerId], stationLabel, dataPointLabel);
                if (childChartRef.current) {
                    childChartRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }
        }
    };

    const handleChildChartClick = (_event: any, _elements: any, chart: any) => {
        try {
            const deviceLabel = chart.tooltip?.dataPoints[0]?.label;
            const dataPointLabel = chart.tooltip?.dataPoints[0]?.dataset?.label;
            const deviceType = deviceTypeList.find((deviceType) => deviceType.label === deviceLabel);
            const url = "/mis-reports";
            if (dataPointLabel === "Expected Range") {
                const params: any = {
                    selected_tab: "MAINTENANCE_DEFICIENCY",
                    stn_codes: assetHealthChartAttribute.station || "",
                    deviceType: deviceType?.value,
                    assetHealth: "Healthy"
                }
                navigate(encodeURI(`${url}?${new URLSearchParams(params).toString()}`));
            } else {
                const params: any = {
                    selected_tab: "MAINTENANCE_DEFICIENCY",
                    stn_codes: assetHealthChartAttribute.station || "",
                    deviceType: deviceType?.value,
                    assetHealth: "Unhealthy"
                }
                navigate(encodeURI(`${url}?${new URLSearchParams(params).toString()}`));
            }
        } catch (error: any) {
            console.log("REDIRECT_TO_ASSET_DETAILS_ERROR", error);
        }
    }

    const getAssetsHealthData = async (customerIds: string[], stationLabel: string, dataPointLabel?: string) => {
        try {
            const url = `${APP_BASE_URL}${GET_ASSET_HEALTH}`;
            const params = {
                customerIds: customerIds.toString()
            }
            const res = await axios.get(url, { params });
            const data = res?.data?.data || [];

            if (singleCustomer && !stationLabel) {
                const dataPacket = {
                    customerName: singleCustomer.customerName,
                    healthyCount: data.reduce((acc: number, curr: any) => acc + curr.healthyCount, 0) || 0,
                    warningCount: data.reduce((acc: number, curr: any) => acc + curr.warningCount, 0) || 0,
                    criticalCount: data.reduce((acc: number, curr: any) => acc + curr.criticalCount, 0) || 0,
                }
                setAssetHealthData([dataPacket]);
                setAssetHealthStats({
                    healthyCount: dataPacket.healthyCount,
                    warningCount: dataPacket.warningCount,
                    criticalCount: dataPacket.criticalCount,
                });
                setAssetHealthForStation(null);
                setAssetHealthChartAttribute(initialAssetHealthChartAttribute);
                return;
            }
            if (stationLabel) {
                // Set data for single station view
                setAssetHealthForStation(data);
                setAssetHealthChartAttribute({
                    assetHealthType: AssetHealthEnum[dataPointLabel as keyof typeof AssetHealthEnum] || null,
                    station: stationLabel
                });
            } else {
                // clear child chart data
                setAssetHealthForStation(null);
                setAssetHealthChartAttribute(initialAssetHealthChartAttribute);
                // set main chart data
                setAssetHealthStats({
                    healthyCount: data.reduce((acc: number, curr: any) => acc + curr?.healthyCount || 0, 0) || 0,
                    warningCount: data.reduce((acc: number, curr: any) => acc + curr?.warningCount || 0, 0) || 0,
                    criticalCount: data.reduce((acc: number, curr: any) => acc + curr?.criticalCount || 0, 0) || 0
                });
                setAssetHealthData(data);
            }
        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                toast.error(error?.response?.data?.message || "Something went wrong");
            } else {
                toast.error(error?.message || "Something went wrong");
            }
            console.log("ASSETS_HEALTH_DATA_ERROR", error);
        }
    }

    const childChartData = useMemo(() => {
        if (!assetHealthForStation) return null;
        return getChildChartData(assetHealthForStation, assetHealthChartAttribute.assetHealthType || "");
    }, [assetHealthForStation, assetHealthChartAttribute.assetHealthType]);

    const childChartOptions = useMemo(() => {
        let y_max = 0;
        if (assetHealthChartAttribute.assetHealthType === AssetHealthEnum["Expected Range"]) {
            y_max = Math.max(...(assetHealthForStation || []).map((item: any) => item.healthyCount))
        } else if (assetHealthChartAttribute.assetHealthType === AssetHealthEnum["Permissible Range"]) {
            y_max = Math.max(...(assetHealthForStation || []).map((item: any) => item.warningCount))
        } else {
            y_max = Math.max(...(assetHealthForStation || []).map((item: any) => item.criticalCount))
        }
        y_max *= 1.2;

        const childChartOptions: ChartOptions<"bar"> = {
            responsive: true,
            maintainAspectRatio: false,
            layout: {
                padding: {
                    left: 50,
                    right: 50,
                    top: 50,
                    bottom: 50
                }
            },
            onClick: (event, elements, chart) => {
                handleChildChartClick(event, elements, chart)
            },
            plugins: {
                legend: { display: false },
                datalabels: {
                    anchor: 'end',
                    align: 'end',
                    font: {
                        weight: 'bold',
                        size: 12
                    },
                    formatter: v => v.toLocaleString(),
                    clamp: true,
                    clip: true,
                    textAlign: 'center',
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    suggestedMax: y_max,
                    ticks: { callback: v => v.toLocaleString() },
                },
            }
        };

        return childChartOptions;
    }, [assetHealthForStation, assetHealthChartAttribute.assetHealthType])

    return (
        <>
            <div className="asset-health-widget">
                <div className="asset-health-widget-body">
                    <div className="asset-health-widget-body-header">
                        <div className="asset-health-widget-body-header-title">
                            <span className="asset-health-widget-body-header-title-text">Asset health statistics</span>
                            <div className="flex alignCenter gap-1">(
                                <span className="asset-health-widget-body-header-title-text expected-range-text">Expected Range: {assetHealthStats.healthyCount} </span> |
                                <span className="asset-health-widget-body-header-title-text permissible-range-text">Permissible Range: {assetHealthStats.warningCount} </span> |
                                <span className="asset-health-widget-body-header-title-text outside-permissible-range-text">Outside Permissible Range: {assetHealthStats.criticalCount} </span>
                                )</div>
                        </div>
                    </div>
                    <div style={{ height: "480px" }}>
                        {mainChartData &&
                            <Chart
                                className="type-status-count-chart cursor-pointer"
                                type="bar"
                                options={mainChartOptions}
                                data={mainChartData}
                            />
                        }

                    </div>
                </div>
            </div>

            {(assetHealthForStation && childChartData) &&
                <div className="asset-health-widget child-chart-container" ref={childChartRef}>
                    <div className="asset-health-widget-body">
                        <div className="asset-health-widget-body-header">
                            <div className="asset-health-widget-body-header-title">
                                <span className="asset-health-widget-body-header-title-text" style={{ color: colors[assetHealthChartAttribute.assetHealthType as keyof typeof colors] }}>
                                    {assetHealthChartAttribute.station} - {getAssetHealthTypeLabel(assetHealthChartAttribute.assetHealthType || '')}
                                </span>
                            </div>
                        </div>
                        <div style={{ height: "480px" }}>
                            <Chart
                                className="type-status-count-chart cursor-pointer"
                                type="bar"
                                options={childChartOptions}
                                data={childChartData}
                            />
                        </div>
                    </div>
                </div>
            }
        </>
    )
}

export default AssetHealthWidget;